import { computeAgeYears, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/helmetConfig';

const ageDepreciationEur = (ageYears, usedBaselineEur, cfg) => {
  const eol = cfg.end_of_life_years;
  const power = cfg.power;
  const floorPct = cfg.floor_pct;
  // floor relative to new price (usedBaseline = newPrice * 0.82)
  const floorEur = usedBaselineEur * floorPct / 0.82;

  if (ageYears <= 0) return usedBaselineEur;
  if (ageYears >= eol) return floorEur;

  const t = ageYears / eol;
  const decay = Math.pow(1 - t, power);
  return floorEur + (usedBaselineEur - floorEur) * decay;
};

const getDesirability = (mfrKey, mdlKey) => {
  const des = ADJUSTMENTS.model_desirability;
  const mfrDes = des[mfrKey];
  if (!mfrDes) return des._unknown_manufacturer;
  return mfrDes[mdlKey] ?? mfrDes._default ?? des._default_manufacturer;
};

const getColorFactor = (colors) => {
  if (!colors || colors.length === 0) return ADJUSTMENTS.color._default;
  const colorCfg = ADJUSTMENTS.color;
  for (const c of colors) {
    const cLower = c.toLowerCase().trim();
    if (colorCfg.penalty_colors.includes(cLower)) return colorCfg.penalty;
  }
  return colorCfg._default;
};

const getConditionFactor = (condition) => {
  if (!condition) return ADJUSTMENTS.condition._default;
  return ADJUSTMENTS.condition[condition.toLowerCase().trim()] ?? ADJUSTMENTS.condition._default;
};

export const calculateHelmet = ({ manufacturer, model, domYear, condition, colors, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrKey;
  let mdlKey;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    mfrKey = null;
    mdlKey = null;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    mfrKey = manufacturer.toLowerCase().trim();
    mdlKey = model.toLowerCase().trim();
    const mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
  }

  // Initial used factor
  const initialUsedFactor = DEPRECIATION.initial_used_factor;
  const usedBaselineEur = newPriceEur * initialUsedFactor;

  // Age depreciation
  const ageCfg = DEPRECIATION.age;
  const ageYears = computeAgeYears(domYear);
  const ageValueEur = ageDepreciationEur(ageYears, usedBaselineEur, ageCfg);
  const ageFactor = newPriceEur > 0 ? ageValueEur / newPriceEur : 0;
  adjustments.ageFactor = Math.round(ageFactor * 1000) / 1000;

  // Model desirability
  let desirability;
  if (customNewPriceEur) {
    desirability = 1.0;
  } else {
    desirability = getDesirability(mfrKey, mdlKey);
  }
  if (desirability !== 1.0) {
    adjustments.modelDesirability = desirability;
  }

  // Condition
  const conditionFactor = getConditionFactor(condition);
  if (conditionFactor !== 1.0) {
    adjustments.condition = conditionFactor;
  }
  if (condition == null) {
    notes.push("Condition unknown. Assuming 'good' (baseline).");
  }

  // Color
  const colorFactor = getColorFactor(colors);
  if (colorFactor !== 1.0) {
    adjustments.color = colorFactor;
  }

  // Final value
  const totalAdjustment = desirability * conditionFactor * colorFactor;
  let valueEur = ageValueEur * totalAdjustment;

  // Round to nearest 5, enforce floor
  const floorEur = newPriceEur * ageCfg.floor_pct;
  valueEur = roundToNearest(valueEur, 5);
  valueEur = Math.max(valueEur, roundToNearest(floorEur, 5));

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  return {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };
};
